import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { fetchCountries } from "../../redux/Slices/locationSlice";
import {
  getClientInformation,
  postClientInformation,
} from "../../redux/Slices/clientInformationSlice";
import DatePicker from "react-datepicker";
import Loader from "../../components/Common/Loader";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import FormSelect from "../../components/Common/FormSelect";
import ToastNotification from "../../components/Common/ToastNotification";
import { UnitContext } from "../../hooks/UnitProvider";

import moment from "moment-timezone";
import {
  cmToInches,
  inchesToCm,
  kgToLbs,
  lbsToKg,
} from "../../utils/unitConversion";
import {
  formatDate,
  isValidDecimalInput,
  isValidNumberInput,
} from "../../utils/Utils";
import {
  ACTIVITY_LEVEL,
  ACTIVITY_LEVEL_AT_WORK,
  ALLERGIES,
  COOKING_BUDGET,
  COOKING_TIME,
  DIETARY_PREFERENCES,
  INJURIES,
  TRAINING_EQUIPMENT,
  TRAINING_EXPERIENCE,
  TRAINING_LOCATION,
  TRAINING_SCHEDULE,
} from "../../utils/Constant/ClientConstant";

const { Option } = Select;

const DIETARY_CONFLICTS_BY_NAME = {
  Pescatarian: ["Vegan"],
  Vegan: ["Perscatarian", "Pescatarian", "Halal", "Kosher", "Dairy-Free"],
  Halal: ["Vegan", "Kosher"],
  Kosher: ["Vegan", "Halal"],
  "Dairy-Free": ["Vegan"],
};
const ClientInfoTab = ({ selectedClientId, status }) => {
  const dispatch = useDispatch();
  const { unit } = useContext(UnitContext);

  const { profileData } = useSelector((state) => state.profile);
  const { clientData, loading, error, postError, postSuccess } = useSelector(
    (state) => state.clientInformation
  );

  const { countries } = useSelector((state) => state.location);

  const { trans } = useCustomTranslation();
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [toastConfig, setToastConfig] = useState(null);

  const timezonesArr = moment.tz.names().map((tz) => ({
    value: tz,
    label: tz,
  }));

  const coachId = profileData?.coachId;
  const clientId = selectedClientId;

  useEffect(() => {
    if (coachId) {
      setFormData({});
      setIsEditing(false);
      dispatch(getClientInformation({ clientId, coachId }));
      dispatch(fetchCountries());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, clientId, coachId]);

  const formattedDate = (date) => {
    const dateOfBirth = new Date(date);

    // Check if the date is valid (i.e., not "Invalid Date")
    if (isNaN(dateOfBirth.getTime())) {
      return "";
    }

    // Format the valid date as YYYY-MM-DD
    return new Date(
      Date.UTC(
        dateOfBirth.getUTCFullYear(),
        dateOfBirth.getUTCMonth(),
        dateOfBirth.getUTCDate()
      )
    )
      .toISOString()
      .split("T")[0];
  };

  useEffect(() => {
    if (clientData) {
      setFormData({
        coachId: coachId,
        clientId: clientData.clientId,
        name: clientData.name,
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        email: clientData.email,
        phoneNumber: clientData.phoneNumber,
        city: clientData.city,
        country: clientData.country,
        countryId: clientData.countryId,
        timezone: clientData.timezone,
        language: clientData.language,
        height:
          unit === "imperial"
            ? cmToInches(clientData?.height || 0)
            : clientData.height,
        weight:
          unit === "imperial"
            ? kgToLbs(clientData?.weight || 0)
            : clientData.weight,
        dateOfBirth: clientData.dateOfBirth
          ? formattedDate(clientData.dateOfBirth)
          : null,
        measurementSystem: unit || "metric",
        updatedBy: "Admin",
        gender: clientData.gender,
        sexAtBirth: clientData.sexAtBirth,
        metricUnitBMI: clientData.metricUnitBMI,
        usCustomaryUnitBMI: clientData.usCustomaryUnitBMI,
        clientProvidedInputs: clientData.clientProvidedInputs,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData]);

  const countriesArr = countries.map((country) => ({
    value: country.countryId,
    label: country.countryName,
    countryId: country.countryId,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "height" || name === "weight") {
      if (!isValidDecimalInput(value)) return;
    }
    if (name === "phoneNumber") {
      if (!isValidNumberInput(value)) return;
    }

    // eslint-disable-next-line eqeqeq
    if (name == "timeZone") {
      setFormData((prevData) => ({
        ...prevData,
        timezone: value,
      }));
      return;
    }
    if (name === "country") {
      const selectedCountry = countriesArr.find(
        (country) => country.countryId === value
      );
      setFormData((prevData) => ({
        ...prevData,
        countryId: selectedCountry ? selectedCountry.countryId : value,
      }));
      setFormData((prevData) => ({
        ...prevData,
        country: selectedCountry ? selectedCountry.countryName : value,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleDateChange = (rawDate) => {
    setFormData((prevData) => ({
      ...prevData,
      dateOfBirth: formattedDate(rawDate),
    }));
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };
  const validateForm = () => {
    let isValid = true;
    const validationErrors = {};
    // Validate first name
    if (!formData.firstName.trim()) {
      validationErrors.firstName =
        trans("Profile.first") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequired");
      isValid = false;
    } else if (formData.firstName.trim().length < 3) {
      validationErrors.firstName =
        trans("Profile.first") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequiredAndMinLength");
      isValid = false;
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      validationErrors.lastName =
        trans("Common.last") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequired");
      isValid = false;
    } else if (formData.lastName.trim().length < 3) {
      validationErrors.lastName =
        trans("Common.last") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequiredAndMinLength");
      isValid = false;
    }

    // Validate email
    if (!formData?.email || !/\S+@\S+\.\S+/.test(formData?.email)) {
      validationErrors.email = trans("Common.validEmail");
      isValid = false;
    }

    if (!formData?.countryId) {
      validationErrors.country =
        trans("Common.country") + " " + trans("Common.isRequired");
      isValid = false;
    }

    if (!formData?.phoneNumber?.trim()) {
      validationErrors.phoneNumber =
        trans("Common.phoneNumber") + " " + trans("Common.isRequired");
      isValid = false;
    }

    setErrors(validationErrors);

    return isValid;
  };
  const handleSaveClick = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsEditing(false);
      let updatedFormData = { ...formData };

      if (unit === "imperial") {
        if (formData?.height) {
          updatedFormData.height = inchesToCm(formData?.height);
        }
        if (formData.weight) {
          updatedFormData.weight = lbsToKg(formData?.weight);
        }
      }

      dispatch(postClientInformation(updatedFormData));
    }
  };

  const handleQuestionChange = (value, index) => {
    setFormData((prevData) => {
      const updatedQuestions = [...prevData.clientProvidedInputs];
      const updatedQuestion = { ...updatedQuestions[index] };

      // Convert the selected values into a comma-separated string
      const selectedOptionIds = Array.isArray(value)
        ? value
            .map((optionText) => {
              const option = updatedQuestion.options.find(
                (opt) => opt.optionText === optionText
              );
              return option ? option.optionId : null;
            })
            .filter((id) => id !== null) // Filter out null values
            .join(",") // Convert to comma-separated string
        : updatedQuestion.options.find((opt) => opt.optionText === value)
            ?.optionId || "";

      updatedQuestion.selectedOptionIds = `${selectedOptionIds}`;
      updatedQuestions[index] = updatedQuestion;

      return {
        ...prevData,
        clientProvidedInputs: updatedQuestions,
      };
    });
  };

  const isValidDate = (date) => {
    const dateOfBirth = new Date(date);
    return !isNaN(dateOfBirth.getTime());
  };

  const calculateBMI = () => {
    const { weight, height, measurementSystem = "metric" } = formData;

    if (measurementSystem === "metric") {
      return (weight / (height * height)).toFixed(2);
    } else {
      // Imperial: weight in pounds, height in inches
      // Convert height from meters to inches
      const heightInInches = height * 39.37;
      return ((weight / (heightInInches * heightInInches)) * 703).toFixed(2);
    }
  };

  const selectGender = [
    { value: "", label: "Select Gender" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const findCountyName = (countryId) => {
    const country = countries.find(
      (country) => country.countryId === countryId
    );
    const countryName = country ? country.countryName : "N/A";
    return countryName;
  };

  // Toast notification logic
  useEffect(() => {
    if (!toastConfig) {
      if (postSuccess) {
        // Trigger success toast
        setToastConfig({
          type: "success",
          message: postSuccess,
          heading: trans("Common.success"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      } else if (postError) {
        // Trigger error toast
        setToastConfig({
          type: "error",
          message: postError,
          heading: trans("Common.failed"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      } else if (error) {
        setToastConfig({
          type: "error",
          message: error,
          heading: trans("Common.failed"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postError, postSuccess, error, trans]);

  return (
    <div className="formContainer vh-100 overflow-auto overflow-x-hidden">
      {loading ? (
        <Loader />
      ) : Object.keys(formData).length === 0 &&
        formData.constructor === Object ? (
        <div className="my-5 d-flex flex-column justify-content-center align-items-center">
          <p className="mt-3 fs-5">{error}</p>
        </div>
      ) : (
        <div className="clientDetailsInfoTabContainer">
          <Row className="align-items-center">
            <Col>
              <h6>
                {trans("Common.client") + " " + trans("Common.information")}
              </h6>
            </Col>
            <Col className="text-end align-items-center">
              {isEditing ? (
                <Button
                  className="saveButton"
                  onClick={handleSaveClick}
                  disabled={status}
                >
                  <i className="bi bi-save mr-1"></i>
                  {trans("Common.save")}
                </Button>
              ) : (
                <Button
                  className="saveButton"
                  onClick={handleEditClick}
                  disabled={status}
                >
                  <i className="bi bi-pencil mr-1"></i>
                  {trans("Common.edit")}
                </Button>
              )}
            </Col>
          </Row>

          <Form>
            <Row>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formFirstName">
                  <FormLabel
                    label={trans("Common.firstName")}
                    className="text-color-gray-40"
                    required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="text"
                      placeholder={
                        trans("Common.enter") + " " + trans("Common.firstName")
                      }
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      isInvalid={!!errors.firstName}
                      feedback={errors.firstName}
                      className="rounded-3"
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.firstName || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formLastName">
                  <FormLabel
                    label={trans("Common.lastName")}
                    className="text-color-gray-40"
                    required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="text"
                      placeholder={
                        trans("Common.enter") + " " + trans("Common.lastName")
                      }
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      isInvalid={!!errors.lastName}
                      feedback={errors.lastName}
                      className="rounded-3"
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.lastName || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formEmail">
                  <FormLabel
                    label={trans("Common.email")}
                    className="text-color-gray-40"
                    required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="email"
                      placeholder={
                        trans("Common.enter") + " " + trans("Common.email")
                      }
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      feedback={errors.email}
                      className="rounded-3"
                      isDisabled={true}
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.email || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formPhone">
                  <FormLabel
                    label={trans("Common.phoneNumber")}
                    className="text-color-gray-40"
                    required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="text"
                      placeholder={
                        trans("Common.enter") +
                        " " +
                        trans("Common.phoneNumber")
                      }
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      isInvalid={!!errors.phoneNumber}
                      feedback={errors.phoneNumber}
                      className="rounded-3"
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.phoneNumber || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formMeasurement">
                  <FormLabel
                    label={trans("Common.measurements")}
                    className="text-color-gray-40"
                  />
                  <br />
                  <FormLabel
                    label={
                      formData?.measurementSystem
                        ? formData.measurementSystem.charAt(0).toUpperCase() +
                          formData.measurementSystem.slice(1)
                        : ""
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formTimezone">
                  <FormLabel
                    label={trans("Client.timeZone")}
                    className="text-color-gray-40"
                  />
                  {isEditing ? (
                    <FormSelect
                      name="timeZone"
                      value={formData.timezone}
                      onChange={handleChange}
                      options={[
                        { value: "", label: trans("Common.selectTimeZone") },
                        ...timezonesArr,
                      ]}
                      feedback={errors.country}
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.timezone} />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formCountry">
                  {isEditing ? (
                    <FormSelect
                      label={trans("Common.country")}
                      name="country"
                      value={formData.countryId}
                      onChange={handleChange}
                      options={[
                        { value: "", label: trans("Common.selectCountry") },
                        ...countriesArr,
                      ]}
                      isInvalid={!!errors.country}
                      feedback={errors.country}
                      labelRequired
                    />
                  ) : (
                    <>
                      <FormLabel
                        label={trans("Common.country")}
                        className="text-color-gray-40"
                      />
                      <br />
                      <FormLabel label={findCountyName(formData.countryId)} />
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <h6>{"Body Details"}</h6>
            <Row>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formHeight">
                  <FormLabel
                    label={trans("Common.height")}
                    className="text-color-gray-40"
                    // required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="text"
                      placeholder={
                        trans("Common.enter") + " " + trans("Common.height")
                      }
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      isInvalid={!!errors.height}
                      feedback={errors.height}
                      className="rounded-3"
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel
                        label={
                          formData?.height
                            ? `${formData.height} ${
                                unit === "imperial" ? "in" : "cm"
                              }`
                            : "-"
                        }
                      />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formWeight">
                  <FormLabel
                    label={trans("Common.weight")}
                    className="text-color-gray-40"
                    // required={isEditing}
                  />
                  {isEditing ? (
                    <FormControl
                      type="text"
                      placeholder={
                        trans("Common.enter") + " " + trans("Common.weight")
                      }
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      isInvalid={!!errors.weight}
                      feedback={errors.weight}
                      className="rounded-3"
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel
                        label={
                          formData?.weight
                            ? `${formData.weight} ${
                                unit === "imperial" ? "lbs" : "kg"
                              }`
                            : "-"
                        }
                      />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formDOB">
                  <FormLabel
                    label={trans("Common.dateOfBirth")}
                    className="text-color-gray-40"
                    // required={isEditing}
                  />
                  {isEditing ? (
                    <div className="d-flex flex-column">
                      <div
                        className="position-relative date-picker"
                        style={{ width: "fit-content" }}
                      >
                        <DatePicker
                          selected={
                            formData.dateOfBirth
                              ? isValidDate(formData.dateOfBirth)
                                ? formattedDate(formData.dateOfBirth)
                                : null
                              : null
                          }
                          onChange={handleDateChange}
                          dateFormat="MMM d, yyyy"
                          placeholderText={trans("Common.select")}
                          className={`custom-datepicker w-100 ${
                            errors.dateOfBirth ? "is-invalid" : ""
                          }`}
                          maxDate={new Date("2018-12-31")}
                          // minDate={new Date()}
                        />
                        <i className="bi bi-calendar3 calendar-icon"></i>
                      </div>

                      {errors.dateOfBirth && (
                        <div className="invalid-feedback d-block">
                          {errors.dateOfBirth}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <br />
                      <FormLabel
                        label={
                          formData.dateOfBirth
                            ? formatDate(formData.dateOfBirth)
                            : "-"
                        }
                      />
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formBMI">
                  <FormLabel
                    label={trans("Client.bmi")}
                    className="text-color-gray-40"
                  />
                  <br />
                  <FormLabel label={calculateBMI()} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formSexAssignedAtBirth">
                  <FormLabel
                    label={trans("Client.sexAssignedAtBirth")}
                    className="text-color-gray-40"
                  />
                  {isEditing ? (
                    <FormSelect
                      label=""
                      name="sexAtBirth"
                      value={formData.sexAtBirth}
                      onChange={handleChange}
                      options={selectGender.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.sexAtBirth || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4} lg={4} className="mt-3">
                <Form.Group controlId="formGender">
                  <FormLabel
                    label={trans("Common.gender")}
                    className="text-color-gray-40"
                    // required={isEditing}
                  />
                  {isEditing ? (
                    <FormSelect
                      label=""
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      options={selectGender.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                    />
                  ) : (
                    <>
                      <br />
                      <FormLabel label={formData.gender || "-"} />
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <h6>{"Additional Information"}</h6>
            <Row>
              {formData?.clientProvidedInputs?.map((question, index) => {
                // Determine selected answers based on selectedOptionIds
                const selectedOptionTexts = Array.isArray(
                  question.selectedOptionIds
                )
                  ? question.selectedOptionIds
                      .split(",")
                      .map(
                        (id) =>
                          question.options.find(
                            (opt) => opt.optionId === Number(id)
                          )?.optionText
                      )
                      // Filter out null values
                      .filter((text) => text !== null)
                  : question.options.find(
                      (opt) =>
                        opt.optionId === Number(question.selectedOptionIds)
                    )?.optionText || "";

                const selectedOptionIds = Array.isArray(
                  question.selectedOptionIds
                )
                  ? question.selectedOptionIds
                  : String(question.selectedOptionIds || "")
                      .split(",")
                      .map(Number);

                const selectedOptionsText = question.options
                  .filter((option) =>
                    selectedOptionIds.includes(option.optionId)
                  )
                  .map((option) => option.optionText)
                  .join(", ");

                return (
                  <>
                    {[
                      ACTIVITY_LEVEL,
                      ACTIVITY_LEVEL_AT_WORK,
                      COOKING_BUDGET,
                      COOKING_TIME,
                      DIETARY_PREFERENCES,
                      INJURIES,
                      TRAINING_SCHEDULE,
                      TRAINING_EXPERIENCE,
                      TRAINING_LOCATION,
                      TRAINING_EQUIPMENT,
                      ALLERGIES,
                    ].includes(question.title) && (
                      <Col xs={12} md={4} lg={4} className="mt-3" key={index}>
                        <Form.Group
                          controlId={`formQuestion${question.questionId}`}
                        >
                          <FormLabel
                            label={question.title}
                            className="text-color-gray-40"
                          />
                          {isEditing ? (
                            [
                              DIETARY_PREFERENCES,
                              ALLERGIES,
                              TRAINING_SCHEDULE,
                              TRAINING_LOCATION,
                              TRAINING_EQUIPMENT,
                            ].includes(question.title) ? (
                              <Select
                                mode="multiple"
                                showSearch
                                optionFilterProp="children"
                                placeholder={`Select ${question.title}`}
                                value={
                                  question.selectedOptionIds
                                    ? question.selectedOptionIds
                                        .split(",")
                                        .map((id) => {
                                          const option = question.options.find(
                                            (opt) => opt.optionId === Number(id)
                                          );
                                          return option
                                            ? option.optionText
                                            : null;
                                        })
                                        // Filter out null values
                                        .filter((text) => text !== null)
                                    : []
                                }
                                onChange={(value) =>
                                  handleQuestionChange(value, index)
                                }
                                className="w-100"
                              >
                                {question?.options?.map((option) => {
                                  const selected = question.selectedOptionIds
                                    ? question.selectedOptionIds
                                        .split(",")
                                        .map((id) => {
                                          const opt = question.options.find(
                                            (o) => o.optionId === Number(id)
                                          );
                                          return opt?.optionText;
                                        })
                                        .filter(Boolean)
                                    : [];

                                  // Flatten all conflicts of selected values
                                  const conflicts = selected.flatMap(
                                    (sel) =>
                                      DIETARY_CONFLICTS_BY_NAME[sel] || []
                                  );

                                  const isDisabled =
                                    question.title === DIETARY_PREFERENCES &&
                                    selected.length > 0 &&
                                    !selected.includes(option.optionText) &&
                                    conflicts.includes(option.optionText);

                                  return (
                                    <Option
                                      key={option.optionId}
                                      value={option.optionText}
                                      disabled={isDisabled}
                                    >
                                      {option.optionText}
                                    </Option>
                                  );
                                })}
                              </Select>
                            ) : (
                              <>
                                <Form.Select
                                  name={`question${question.questionId}`}
                                  value={selectedOptionTexts}
                                  onChange={(e) =>
                                    handleQuestionChange(e.target.value, index)
                                  }
                                  className="rounded-3"
                                >
                                  <option
                                    value=""
                                    disabled
                                    selected={!selectedOptionTexts}
                                  >
                                    Please select
                                  </option>
                                  {question?.options?.map((option) => (
                                    <option
                                      key={option.optionId}
                                      value={option.optionText}
                                    >
                                      {option.optionText}
                                    </option>
                                  ))}
                                </Form.Select>
                              </>
                            )
                          ) : (
                            <>
                              <br />
                              <FormLabel label={selectedOptionsText || "-"} />
                            </>
                          )}
                        </Form.Group>
                      </Col>
                    )}
                  </>
                );
              })}
            </Row>
          </Form>
        </div>
      )}

      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientInfoTab;
